import type { DocNode } from '@atlaskit/adf-schema';
import { areNodesEqualIgnoreAttrs, isEmptyDocument } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type {
	DetectionLayer,
	OutcomeClass,
} from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import { normaliseAIExperienceEvent } from '@atlassian/editor-ai-common/utils/normaliseEvents';
import type { EditorPluginAIActions } from '@atlassian/editor-plugin-ai';
import { UNSTABLE_configItems } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import type { API, StreamingCommand } from '../aiStreamingOrchestratorPluginType';
import type { InitOptions, QueuedStreamCommand } from '../types/StreamCommandQueue';

import { DebugLogger } from './aifc-logging';
import {
	checkAndFireNoDocumentChangeAnalytics,
	createFireADFStreamingAnalyticsEvent,
	fireAttributeOnlyReplaceNodeAnalytics,
	fireSessionOutcomeAnalytics,
	fireSessionStartedAnalytics,
	fireToolCallAnomaly,
	fireToolCallCapturedAnalytics,
	fireCommandError,
} from './analytics';
import {
	analyzeOutcome,
	type DocLike,
	type OutcomeAnalysis,
	type ToolCallRecord,
} from './post-apply-outcome-analyzer';
import { classifyApplyError } from './utils/classify-apply-error';
import { extractExplicitRemovalTargets } from './utils/extract-explicit-removal-targets';
import { collectAllLocalIds, findNodeByLocalIdAndApi } from './utils/find-node-by-local-id';
import { getPmDoc } from './utils/get-pm-doc';
import { isAStreamableCommand } from './utils/is-a-streamable-command';
import { makeDocumentValid } from './utils/make-document-valid';
import { mergeStreamCommands } from './utils/merge-stream-commands';
import { createTimeoutWrapper } from './utils/timeout-wrapper';
import { updateInsertAfterDoc } from './utils/update-insert-after-doc';

import { createStreamingCommands } from './index';

type EditorPluginAIEvent = Parameters<EditorPluginAIActions['dispatchAIEvent']>[0];

/**
 * Returns the wire-name of the streaming command associated with a failed
 * apply attempt, suitable for the `commandType` attribute on
 * `tool_call_apply_failed` events.
 *
 * Prefers the active `StreamingCommand` (e.g. `'replaceNode'`,
 * `'appendNodeToEnd'`) when available, falling back to the queue-level
 * discriminant (`'streamCommand'`, `'endStream'`, `'initStream'`) and finally
 * to `'unknown'`.
 */
function extractCommandType(
	queuedCommand: QueuedStreamCommand | null,
	activeCommand: StreamingCommand | undefined,
): string {
	if (queuedCommand?.type === 'initStream') {
		return queuedCommand.command.type;
	}
	if (activeCommand?.type) {
		return activeCommand.type;
	}
	return queuedCommand?.type ?? 'unknown';
}

/**
 * Returns the `localId` of the node targeted by a failed apply attempt, if
 * one can be derived from the active command. Returns `undefined` for
 * commands that operate without a target node (e.g. `appendNodeToEnd`,
 * `getPageContent`) or when no command is in flight.
 */
function extractTargetLocalId(
	queuedCommand: QueuedStreamCommand | null,
	activeCommand: StreamingCommand | undefined,
): string | undefined {
	const command = queuedCommand?.type === 'initStream' ? queuedCommand.command : activeCommand;
	if (command && 'localId' in command && typeof command.localId === 'string') {
		return command.localId;
	}
	return undefined;
}

/**
 * Command types whose execution mutates the document. Used by the post-apply
 * outcome analyzer to decide whether `noMeaningfulEffect` should fire — a
 * session that ran nothing but read-only commands cannot be flagged for "no
 * meaningful effect" because no effect was ever requested.
 */
const MUTATING_COMMAND_TYPES: ReadonlySet<string> = new Set([
	'append',
	'appendNode',
	'appendNodeToEnd',
	'replaceNode',
	'removeNode',
	'removeNodes',
	'setAttrs',
	'wrap',
	'wrapNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'moveNode',
]);

/**
 * Returns the `localId` field for a streaming command that targets a
 * single existing node, or `undefined` for commands without one
 * (`appendNode`, `appendNodeToEnd`) or whose target uses a different
 * shape (`removeNodes` uses `localIds[]` plural, not a single `localId`).
 *
 * Used by the pre-apply target-resolution check so we only fire
 * `local_id_not_found` when the command actually expected a node
 * by ID.
 */
function getTargetLocalId(command: StreamingCommand): string | undefined {
	if ('localId' in command && typeof command.localId === 'string') {
		return command.localId;
	}
	return undefined;
}

/**
 * Convert a `StreamingCommand` into the condensed `ToolCallRecord` shape
 * consumed by `analyzeOutcome`. Captures the command type and any localId
 * targets (single or list-shaped). Used to build the per-session tool-call
 * record so the analyzer can distinguish intentional removals/modifications
 * from collateral damage.
 */
function toToolCallRecord(command: StreamingCommand): ToolCallRecord {
	const targetLocalId = getTargetLocalId(command);
	const targetLocalIds =
		'localIds' in command && Array.isArray((command as { localIds?: unknown }).localIds)
			? ((command as { localIds: string[] }).localIds.filter(
					(id): id is string => typeof id === 'string',
				) as string[])
			: undefined;
	return {
		commandType: command.type,
		...(targetLocalId !== undefined ? { targetLocalId } : {}),
		...(targetLocalIds !== undefined && targetLocalIds.length > 0 ? { targetLocalIds } : {}),
	};
}

export class StreamCommandQueue {
	private queue: QueuedStreamCommand[] = [];
	private adfStreamer: ADFStreamer | undefined;
	private streamingCommands: ReturnType<typeof createStreamingCommands>;
	private currentCommand: StreamingCommand | undefined = undefined;
	private docBeforeStreaming: PMNode | undefined = undefined;
	private isStreaming = false;
	private isFinalStream = false;
	private aborted: string[] = [];
	private subscription: (() => void) | null = null;
	private previousValidDoc: { adfDoc?: DocNode; doc?: PMNode } | null = null;
	private logger: DebugLogger;
	private endedActionIds: string[] = [];
	private currentAction: {
		id: string;
		initialNode: PMNode | undefined;
		isCreateAction: boolean;
		type: StreamingCommand['type'];
	} | null = null;
	private insertAfterQueueLocalId: string | undefined = undefined;
	/**
	 * Cached full-depth localId snapshot from the most recent
	 * applyCommand post-apply pass. Built via `collectAllLocalIds`, so
	 * it contains every localId at every depth in the doc — not just
	 * top-level. Reused as the pre-apply snapshot of the *next*
	 * applyCommand to avoid re-walking the document twice per command.
	 * Invalidated at session boundaries (start/end/abort/destroy) and
	 * when the apply-anomaly diagnostic gate is off.
	 */
	private lastPostApplyLocalIds: Set<string> | undefined = undefined;
	/**
	 * The traceId received in response header from AI api call
	 * This is used to trace the AI experience through the backend to frontend.
	 */
	private traceId: string | undefined = undefined;
	/**
	 * The singleInstrumentationId for tracking the AI experience
	 * This is used to correlate events across multiple stream processing passes
	 */
	private singleInstrumentationId: string | undefined = undefined;
	/**
	 * The promptVersion from action metadata
	 * This is used to track which version of the prompt was used for analytics
	 */
	private promptVersion: string | undefined = undefined;
	/**
	 * Aggregated repair signals across every streaming action in the current
	 * session, used to fire the authoritative `sessionOutcome` event exactly
	 * once per session.
	 *
	 * `*Invalid` aggregates with OR (any action that observed an invalid layer
	 * marks the session as having seen an invalid layer).
	 *
	 * `*UnrepairedCount` counts how many actions had an invalid layer that was
	 * NOT recovered. From these we derive a correct session-level "repaired"
	 * value as: `*Invalid && *UnrepairedCount === 0` — i.e. every invalid
	 * occurrence in the session was recovered.
	 *
	 * This avoids a misleading `success_with_repairs` classification when an
	 * earlier action repaired successfully but a later action failed to repair:
	 * a naive OR over `*Repaired` would incorrectly flatten the irrecoverable
	 * failure into a success.
	 */
	private sessionRepairSignals: {
		anyAdfInvalid: boolean;
		anyJsonInvalid: boolean;
		unrepairedAdfCount: number;
		unrepairedJsonCount: number;
	} = {
		anyJsonInvalid: false,
		anyAdfInvalid: false,
		unrepairedJsonCount: 0,
		unrepairedAdfCount: 0,
	};
	/**
	 * Tracks whether the `sessionOutcome` analytics event has already been fired
	 * for the active session. Ensures the event fires at most once per session.
	 */
	private sessionOutcomeFired = false;

	/**
	 * Per-session record of every tool call processed by `applyCommand`.
	 * Consumed at end-of-session by `analyzeOutcome` to compute
	 * `affectedLocalIds`. Reset at all session boundaries. Only populated
	 * when the `platform_editor_ai_normalized_telemetry` experiment is on.
	 */
	private sessionToolCalls: ToolCallRecord[] = [];

	/**
	 * The most recent `analyzeOutcome` result, computed at end-of-session.
	 * Read by `fireSessionOutcomeOnce` (added by CCI-15893) to enrich the
	 * `sessionOutcome` event with structural-delta fields. Undefined until
	 * the first end-of-session analysis runs in the active lifetime, and
	 * reset at all session boundaries.
	 */
	private lastOutcomeAnalysis: OutcomeAnalysis | undefined = undefined;

	/**
	 * Snapshot of the editor document at session start, captured by
	 * `startSession`. Consumed at end-of-session by `analyzeOutcome` as
	 * `preSessionAdf`. Distinct from `docBeforeStreaming`, which is
	 * reassigned per command for the per-command repair-signal pipeline.
	 * Reset at all session boundaries. Only populated when the
	 * `platform_editor_ai_normalized_telemetry` experiment is on.
	 */
	private preSessionDoc: PMNode | undefined = undefined;

	constructor(
		private readonly api: API,
		private readonly streamFlowPacer: StreamFlowPacer | null = null,
	) {
		this.logger = new DebugLogger(api);
		this.streamingCommands = createStreamingCommands(api);
	}

	private isActorReady(): boolean {
		try {
			const currentState = this.api.aiExperience.sharedState.currentState();
			if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				// Check if actor exists and is in streaming state
				const actor = this.api.aiExperience.actions.getAiExperienceActor();
				return actor && currentState?.isStreaming === true;
			}
			return currentState?.isStreaming === true;
		} catch {
			return false;
		}
	}

	private commandToProcess(commandToProcess: QueuedStreamCommand): Promise<void> {
		const { type, actionId, chunk } = commandToProcess;

		if (type === 'initStream') {
			return this.initStreamPromise(commandToProcess.command, commandToProcess.options);
		} else if (type === 'endStream') {
			return this.endExperiencePromise(commandToProcess);
		} else {
			return this.processStreamCommandPromise(actionId, chunk);
		}
	}

	private processingQueue = false;
	private inProcessing: string[] = [];
	private queueTimeout: NodeJS.Timeout | null = null;
	// This is only used *if* we have started receiving values with a different
	// action id (new stream) and we have nothing left in the current stream (ie. it hasn't finished)
	// Really this shouldn't be hit since this would only happen on the edge when we're wrapping up the old stream
	// and starting the next early
	private CURRENT_QUEUE_TIMEOUT = 5000;
	private currentCommandTimeoutReset: (() => void) | null = null;

	private async processQueue() {
		if (this.processingQueue) {
			return;
		}
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.processingQueue = true;
		let commandsToProcess = [...this.queue].filter(
			(cmd) => cmd.actionId === this.currentAction?.id,
		);
		this.inProcessing = [...new Set(commandsToProcess.map((cmd) => cmd.actionId))];
		this.queue = this.queue.filter((cmd) => !commandsToProcess.includes(cmd));
		let currentCommand = null;

		try {
			commandsToProcess = mergeStreamCommands(commandsToProcess);
			for (const commandToProcess of commandsToProcess) {
				currentCommand = commandToProcess;
				if (this.aborted.includes(commandToProcess.actionId)) {
					// If we abort the last stream let's move onto the next stream
					if (commandToProcess.type === 'endStream') {
						this.resolvePendingQueue(true);
					}
					continue;
				}
				await this.wrapInTimeout(this.commandToProcess(commandToProcess), commandToProcess);
			}
		} catch (error) {
			if (expValEquals('platform_editor_ai_fix_insert_after_selection', 'isEnabled', true)) {
				fireCommandError({
					api: this.api,
					traceId: this.traceId,
					command: currentCommand,
					isCreateAction: this.currentAction?.isCreateAction,
					error,
				});
				// Fire the unified tool_call_anomaly event in parallel —
				// keeps the existing fireCommandError event flowing for
				// legacy dashboards while attribution-first dashboards
				// consume the new event.
				if (expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
					const { symptom } = classifyApplyError(error);
					fireToolCallAnomaly({
						api: this.api,
						traceId: this.traceId,
						commandType: extractCommandType(currentCommand, this.currentCommand),
						targetLocalId: extractTargetLocalId(currentCommand, this.currentCommand),
						errorMessage: ErrorUtils.extractErrorMessage(error),
						symptom,
					});
				}
			} else {
				throw error;
			}
		} finally {
			// Reset the processing queue flag
			this.processingQueue = false;
			const lastCommand = commandsToProcess.pop();
			this.inProcessing = [];
			if (lastCommand?.type === 'endStream') {
				this.checkAndResolveEmptyQueue();
			}
		}
		// If commands have been added to the queue during async operations, process them now
		if (this.queue.filter((cmd) => cmd.actionId === this.currentAction?.id).length > 0) {
			this.processQueue();
		} else if (this.queue.length > 0) {
			this.queueTimeout = setTimeout(() => {
				this.logger.logError(`Current stream queue timed out while starting next`, {
					actionId: this.currentAction?.id ?? undefined,
				});
				this.resolvePendingQueue(true);
				this.processQueue();
			}, this.CURRENT_QUEUE_TIMEOUT);
		}
	}

	private onEmptyQueueResolve: (() => void) | null = null;
	public onEmptyQueue(): Promise<void> {
		const promise = new Promise<void>((resolve) => {
			this.onEmptyQueueResolve = resolve;
		});
		this.checkAndResolveEmptyQueue();
		return promise;
	}

	// 2 seconds - this is the maximum time to process a command before we abort it and continue
	// This can be longer if for example if a lot of commands have been processed, but the stream flow
	// pacer has a backlog (particularly for elements with lots of attributes / marks etc) and hasn't had
	// a chance to emit yet
	private COMMAND_TIMEOUT = 2000;

	private async wrapInTimeout(promise: Promise<void>, cmd: QueuedStreamCommand): Promise<void> {
		const { promise: wrappedPromise, resetTimeout } = createTimeoutWrapper(promise, {
			timeout: this.COMMAND_TIMEOUT,
			onTimeout: () => {
				this.logger.logError(`Stream command timed out ${cmd.type}`, {
					actionId: cmd.actionId,
					chunk: cmd.chunk,
				});
			},
			onCleanup: () => {
				this.currentCommandTimeoutReset = null;

				if (cmd.type === 'endStream') {
					this.checkAndResolveEmptyQueue();
				}
				this.resolvePendingQueue();
			},
		});

		this.currentCommandTimeoutReset = resetTimeout;
		await wrappedPromise;
	}

	/**
	 * End the AI experience
	 */
	public endStreamCommand(actionId: string): void {
		// duplicated endStreamCommand will kill accept reject screen prematurely
		if (this.endedActionIds.includes(actionId)) {
			this.logger.logPublic('End Stream Command Ignored', { actionId });
			return;
		}
		this.endedActionIds.push(actionId);
		if (this.endedActionIds.length > 10) {
			this.endedActionIds.shift();
		}

		this.logger.logPublic('End Stream Command', { actionId });
		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({ type: 'endStream', actionId, chunk: undefined, reason: 'endCurrentStream' });
		this.processQueue();
	}

	private endExperience() {
		// if it's isFinalStream
		// - when auto accept AI changes is on, editor-plugin-ai will end itself
		// - when auto accept AI changes is off, the accept reject screen will be shown
		// if it's not isFinalStream, the endAIExperience is used to kill a previous AI modal
		// - which can be the previous accept reject screen
		// - or the command palette screen (followed by rovo free-gen screen)
		if (!this.isFinalStream) {
			this.logger.logQueueState('End AI Experience');
			this.api.aiExperience.actions.endAIExperience();
		}
		this.isFinalStream = false;
		this.adfStreamer?.reset();
		this.streamFlowPacer?.reset();
		this.subscription?.();
	}
	private isPendingResolveQueue = false;

	private resolvePendingQueue(timeout = false) {
		if (timeout || this.isPendingResolveQueue) {
			const currentAction = this.queue.find((cmd) => cmd.type === 'initStream') ?? null;
			this.logger.logQueueState('Resolve Pending Queue', {
				actionId: currentAction?.actionId,
				reason: timeout ? 'timeout' : 'isPendingResolveQueue',
			});
			if (timeout && expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
				// Queue-stall timeout fired (no further commands arrived).
				// Idempotent via `fireSessionOutcomeOnce`'s once-per-session guard.
				this.fireSessionOutcomeOnce({ lifecycleSymptom: 'session_timeout' });
			}
			this.currentAction = currentAction
				? {
						id: currentAction.actionId,
						isCreateAction:
							(currentAction.command.type === 'appendNode' ||
								currentAction.command.type === 'appendNodeToEnd') &&
							currentAction.command.streamingType === 'create',
						initialNode: findNodeByLocalIdAndApi(
							this.api,
							// replaceDoc has no localId — only replaceNode needs one here.
							currentAction.command.type === 'replaceNode' && currentAction.command.localId,
						),
						type: currentAction.command.type,
					}
				: null;
			this.isPendingResolveQueue = false;
		}
	}

	private checkAndResolveEmptyQueue() {
		if (this.queue.length === 0 && this.inProcessing.length === 0) {
			this.onEmptyQueueResolve?.();
		}
	}

	private endNonStreamble() {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			this.api.aiExperience.actions.dispatchAIEvent({
				type: 'complete',
				adfResponse: undefined,
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			send({
				type: 'complete',
				markdown: '',
				adfResponse: undefined,
				cacheKey: '',
				modelInput: {},
			});
		}
	}

	private async endExperiencePromise(
		command: Extract<QueuedStreamCommand, { type: 'endStream' }>,
	): Promise<void> {
		const { actionId, reason } = command;
		this.logger.logQueueState('End Experience', { actionId, reason });
		this.isPendingResolveQueue = reason === 'endCurrentStream';
		// should not check empty queue if the end command is triggered by the initStreamCommand
		// which is used to close a previous AI modal
		// because there are more commands to process even if the queue is empty
		// and this.isPendingResolveQueue will be reset, so use a different flag here
		const shouldCheckEmptyQueue = reason === 'endCurrentStream';
		const isStreamingCommand = !(
			this.currentAction?.type && !isAStreamableCommand({ type: this.currentAction?.type })
		);

		if (this.isStreaming && isStreamingCommand) {
			await this.processStreamCommandPromise(actionId, '', true);
			if (this.currentAction?.type === 'replaceNode') {
				fireAttributeOnlyReplaceNodeAnalytics({
					api: this.api,
					initialNode: this.currentAction.initialNode,
					lastDoc: this.adfStreamer?.getLastDoc(),
				});
			}
			// replaceDoc: fire analytics on finish but no attribute-only check (no localId / initialNode).
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: this.currentCommand,
				docBeforeStreaming: this.docBeforeStreaming,
				traceId: this.traceId,
			});
		} else if (!isStreamingCommand) {
			this.isFinalStream = true;
		}
		return new Promise<void>((resolve) => {
			const endNow = () => {
				this.endExperience();
				// After we end the experience there are a number of React re-renders that occur immediately after
				// This causes the state to flick between streaming and not streaming, it's not pretty
				// but wrap this in a `requestAnimationFrame` to delay our check until this clears.
				// To fix this properly we probably need to decouple the state with React
				const checkStreaming = () => {
					const resolveAndEndStream = () => {
						resolve();
						this.logger.logQueueState('End Experience Resolved', { actionId, reason });
						this.resolvePendingQueue();
						shouldCheckEmptyQueue && this.checkAndResolveEmptyQueue();
					};
					if (!isStreamingCommand) {
						this.endNonStreamble();
						resolveAndEndStream();
						return;
					}
					if (!this.api.aiExperience?.sharedState.currentState()?.isStreaming) {
						resolveAndEndStream();
						return;
					}
					const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
						if (!nextSharedState.isStreaming) {
							unsubscribe();
							resolveAndEndStream();
						}
					});
				};

				setTimeout(checkStreaming, 0);
			};

			// If we're not running the pacer or its a non-streamable command, end immediately
			if (
				!this.streamFlowPacer?.isRunning() ||
				(this.currentAction?.type && !isAStreamableCommand({ type: this.currentAction?.type }))
			) {
				endNow();
			} else {
				this.streamFlowPacer.onComplete(() => {
					// Need to be careful not to end the experience too soon. We need to give the xstate and react time to process
					// the final stream events before we kill the experience. Otherwise you will see an unfinished stream in the doc.
					setTimeout(endNow, 100);
				});
			}
		});
	}

	/**
	 * Streams the AI experience
	 */
	public queueStreamCommand(actionId: string, chunk: string): void {
		this.logger.logPublic('Stream Command', { actionId, chunk });
		if (this.aborted.includes(actionId) || this.isAborted) {
			this.updateAbortedCache([actionId]);
			return;
		}
		// Actor not ready, queue the command
		this.queue.push({ type: 'streamCommand', actionId, chunk });
		if (this.isActorReady()) {
			this.processQueue();
		} else {
			// if there are multiple sequential queueStreamCommand calls
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api?.aiExperience.sharedState.onChange(() => {
				if (this.isActorReady()) {
					unsubscribe?.();
					this.processQueue();
				}
			});
		}
	}

	private hasChangedDocument(latestDoc: ReturnType<typeof makeDocumentValid>): boolean {
		if (!this.previousValidDoc && latestDoc) {
			return true;
		}

		const nodesAreNotEqual =
			!this.previousValidDoc?.doc ||
			!latestDoc?.doc ||
			!areNodesEqualIgnoreAttrs(this.previousValidDoc?.doc, latestDoc.doc);
		if (this.streamFlowPacer === null) {
			// replaceDoc is allowed to produce an empty document (e.g. "delete entire page"),
			// so we skip the isEmptyDocument guard for that action type.
			const isReplaceDoc = this.currentAction?.type === 'replaceDoc';
			if (isReplaceDoc) {
				// For replaceDoc, latestDoc.doc may be undefined when the streamed content is
				// empty HTML (the adfStreamer returns undefined for empty input). We treat
				// "undefined → previous doc exists" as a change so the empty-page intent
				// is honoured — the final stream path will supply the empty-paragraph fallback.
				return Boolean(nodesAreNotEqual);
			}
			return Boolean(nodesAreNotEqual && latestDoc.doc && !isEmptyDocument(latestDoc.doc));
		}
		const latestCount = this.streamFlowPacer?.adfTextLen(latestDoc?.adfDoc);
		return Boolean(
			nodesAreNotEqual &&
			latestDoc.adfDoc &&
			this.previousValidDoc?.adfDoc &&
			latestCount.chars > 0 &&
			// Ensure we only stream changes if the stream flow pacer detects semantic changes
			!this.streamFlowPacer?.adfTextLen(this.previousValidDoc?.adfDoc).gte(latestCount),
		);
	}

	private createCallback(usePush: boolean) {
		if (usePush) {
			return (evt: EditorPluginAIEvent) => {
				const fullEvent = normaliseAIExperienceEvent(evt);
				this?.streamFlowPacer?.push(fullEvent);
			};
		}

		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			return (evt: EditorPluginAIEvent) => {
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			};
		}

		// Legacy path: send to actor with padded defaults
		const { send } = this.api.aiExperience.actions.getAiExperienceActor();
		return (evt: EditorPluginAIEvent) => {
			const payload = normaliseAIExperienceEvent(evt);
			send(payload as Parameters<typeof send>[0]);
		};
	}

	private processStreamCommand(actionId: string, chunk: string, finalStream = false) {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			this.streamFlowPacer?.setEmit((evt: EditorPluginAIEvent) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				this.api.aiExperience.actions.dispatchAIEvent(evt);
			});
		} else {
			const { send } = this.api.aiExperience.actions.getAiExperienceActor();
			this.streamFlowPacer?.setEmit((evt: Parameters<typeof send>[0]) => {
				// While the stream flow pacer is doing work reset the timeout
				this.currentCommandTimeoutReset?.();
				send(evt);
			});
		}

		// Bypass the pacer when noStream is set (e.g. pre-computed results from semantic tool calls)
		const noStream =
			expValEquals('platform_editor_ai_no_stream_skip_pacer', 'isEnabled', true) &&
			this.currentAction?.type &&
			'noStream' in this.currentAction &&
			this.currentAction.noStream;
		const callback = this.createCallback(!!this.streamFlowPacer?.push && !noStream);

		// Initialize streamer if needed
		if (!this.adfStreamer) {
			const schema = this.api.core.sharedState.currentState()?.schema;
			this.adfStreamer = schema
				? new ADFStreamer(
						schema,
						createFireADFStreamingAnalyticsEvent({
							api: this.api,
							isCreateAction: this.currentAction?.isCreateAction ?? false,
							traceId: fg('platform_editor_ai_traceid_in_analytics_events')
								? this.traceId
								: undefined,
							promptVersion: this.promptVersion,
						}),
					)
				: undefined;
		}

		if (finalStream) {
			const lastDocResult = makeDocumentValid(this.api, this.adfStreamer?.getLastDoc());
			let adfResponseBefore = lastDocResult.adfDoc;

			// replaceDoc with an empty value (e.g. "delete entire page") produces either no
			// ADF or a bare { type:"doc" } with no content from the streamer. Substitute a
			// minimal empty document so the downstream insertion path actually clears the
			// editor content rather than silently no-oping.
			const isFinalEffectivelyEmptyDoc =
				!adfResponseBefore || !adfResponseBefore.content || adfResponseBefore.content.length === 0;
			if (isFinalEffectivelyEmptyDoc && this.currentAction?.type === 'replaceDoc') {
				adfResponseBefore = {
					version: 1,
					type: 'doc',
					content: [{ type: 'paragraph', content: [] }],
				} as DocNode;
			}

			const schema = this.api.core.sharedState.currentState()?.schema;
			const { updatedResponse, localId } = updateInsertAfterDoc(
				adfResponseBefore,
				this.currentAction?.type,
				schema,
			);
			const adfResponse = updatedResponse;
			this.insertAfterQueueLocalId = localId;

			const repairSignals = this.adfStreamer?.endStreamingSession({
				hasProcessedFinalResponse: true,
			});
			if (
				repairSignals &&
				expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)
			) {
				// A session may contain multiple streaming actions. Aggregate
				// repair signals across them so the final `sessionOutcome` event
				// correctly reflects the worst per-action outcome:
				// - `*Invalid`: OR — any invalid action means the session saw
				//   that layer fail at least once.
				// - `unrepaired*Count`: count actions whose invalid layer was
				//   NOT recovered. The session is only considered "fully
				//   repaired" if this count is zero. A naive OR over the
				//   per-action `*Repaired` flags would incorrectly mask a
				//   later irrecoverable failure when an earlier action had
				//   recovered.
				this.sessionRepairSignals = {
					anyJsonInvalid:
						this.sessionRepairSignals.anyJsonInvalid || repairSignals.finalJsonInvalid,
					anyAdfInvalid: this.sessionRepairSignals.anyAdfInvalid || repairSignals.finalAdfInvalid,
					unrepairedJsonCount:
						this.sessionRepairSignals.unrepairedJsonCount +
						(repairSignals.finalJsonInvalid && !repairSignals.finalJsonRepaired ? 1 : 0),
					unrepairedAdfCount:
						this.sessionRepairSignals.unrepairedAdfCount +
						(repairSignals.finalAdfInvalid && !repairSignals.finalAdfRepaired ? 1 : 0),
				};
			}
			this.logger.logQueueState('Final Stream', { actionId, adf: adfResponse, finalStream: true });
			callback({
				type: 'complete',
				adfResponse,
			});
			return true;
		}

		// Resetting because we are going to get full ADF as chunk
		this.adfStreamer?.reset();
		this.adfStreamer?.processChunk(chunk);

		const latestDoc = makeDocumentValid(
			this.api,
			noStream ? this.adfStreamer?.getLastDoc() : this.adfStreamer?.getLastDocWithoutLastLeafNode(),
		);

		const changed = this.hasChangedDocument(latestDoc);
		this.previousValidDoc = latestDoc;

		// We compare the before/after docs because if nothing has changed then it will not trigger a react update (because the adfResponse needs to be different)
		// which means the chunkNumber will never update and the processStreamCommandPromise Promise will never resolve.
		// This cause the wrapInTimeout 1sec delay to occur before the next chunk is processed. This means we can end up in a situation
		// where we're recieving a lot of chunks, but nothing in the view is changed and everything starts taking a lot of time
		// because of the sequential 1sec failovers.
		if (changed) {
			// For replaceDoc with an empty value (e.g. "delete entire page"), the adfStreamer
			// returns either undefined or a bare { type:"doc" } with no content — fall back to
			// a minimal empty document so the editor content is actually cleared.
			const isEffectivelyEmptyDoc =
				!latestDoc.adfDoc || !latestDoc.adfDoc.content || latestDoc.adfDoc.content.length === 0;
			const streamAdfResponse =
				isEffectivelyEmptyDoc && this.currentAction?.type === 'replaceDoc'
					? ({
							version: 1,
							type: 'doc',
							content: [{ type: 'paragraph', content: [] }],
						} as DocNode)
					: latestDoc.adfDoc;
			callback({
				type: 'stream',
				adfResponse: streamAdfResponse,
			});
			return true;
		}

		return false;
	}

	private processStreamCommandPromise(
		actionId: string,
		chunk: string,
		finalStream = false,
	): Promise<void> {
		this.logger.logQueueState('Streaming Command', { actionId, chunk, finalStream });
		this.isStreaming = !finalStream;
		this.isFinalStream = finalStream;
		return new Promise<void>((resolve) => {
			// do not wrap the code in setTimeout, it will pause the streaming when the tab is in background
			const chunkBefore = this.api.aiExperience?.sharedState.currentState()?.chunkNumber;
			if (!this.processStreamCommand(actionId, chunk, finalStream)) {
				// If nothing was processed then we should be able to end immediately since we will never get a chuckNumber update
				// this means the next chunk can be processed straight away.
				resolve();
				this.logger.logQueueState('Streaming Command Resolved', { actionId, chunk, finalStream });
				return;
			}

			if (chunkBefore !== this.api.aiExperience?.sharedState.currentState()?.chunkNumber) {
				resolve();
				this.logger.logQueueState('Streaming Command Resolved', { actionId, chunk, finalStream });
				return;
			}
			// if there are multiple sequential queueStreamCommand calls and one of them timeout
			// and we assign the return value to this.subscription and call this.subscription
			// we won't be able to unsubscribe the corresponding right one
			// and will cause the callback to be called tons of times when state changes
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(
				({ nextSharedState, prevSharedState }) => {
					if (nextSharedState?.chunkNumber !== prevSharedState?.chunkNumber) {
						unsubscribe?.();
						resolve();
						this.logger.logQueueState('Streaming Command Resolved', {
							actionId,
							chunk,
							finalStream,
						});
					}
				},
			);
		});
	}

	/**
	 * Streams the AI experience
	 */
	public initStreamCommand(
		actionId: string,
		command: StreamingCommand,
		options?: InitOptions,
	): void {
		this.logger.logPublic('Init Command', { actionId, command });
		this.isAborted = false;

		// Track tool call captured for analytics
		if (fg('platform_editor_ai_tool_call_received_analytics')) {
			fireToolCallCapturedAnalytics({
				api: this.api,
				command,
				actionId,
				traceId: this.traceId,
			});
		}
		if (!this.currentAction) {
			this.currentAction = {
				id: actionId,
				isCreateAction:
					(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
					command.streamingType === 'create',
				initialNode: findNodeByLocalIdAndApi(
					this.api,
					// replaceDoc has no localId — only pass localId for replaceNode.
					command.type === 'replaceNode' && command.localId,
				),
				type: command.type,
			};
		}
		// If we're already running end the stream and restart
		if (
			this.api.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState.isRunning
		) {
			this.queue.push({
				type: 'endStream',
				actionId,
				chunk: undefined,
				reason: 'endPreviousStream',
			});
		}
		this.queue.push({
			type: 'initStream',
			actionId,
			chunk: undefined,
			command,
			options,
		});
		this.processQueue();
	}

	private applyCommand(command: StreamingCommand) {
		const insertAfterQueue =
			this.currentCommand?.type === 'insertNodeAfter' &&
			command.type === 'insertNodeAfter' &&
			command.localId === this.currentCommand?.localId;

		this.currentCommand = command;
		this.docBeforeStreaming = getPmDoc(this.api);
		let isSuccessful = true;

		// Apply-anomaly diagnostics: see runPre/PostApplyDiagnostics for
		// the symptoms detected. Gated to keep the snapshot/diff cost
		// (and any associated bookkeeping) out of the hot path entirely
		// when the experiment is off.
		const normalizedTelemetryEnabled = expValEquals(
			'platform_editor_ai_normalized_telemetry',
			'isEnabled',
			true,
		);
		// Record this tool call so end-of-session `analyzeOutcome` can
		// distinguish intentional removals/modifications from collateral
		// damage. Only collected when the experiment is on.
		if (normalizedTelemetryEnabled) {
			this.sessionToolCalls.push(toToolCallRecord(command));
		}
		// The localId the apply path will actually attempt to act on.
		// `insertAfterQueue` commands rewrite the localId at apply time
		// so that consecutive `insertNodeAfter` calls chain correctly
		// (see the apply branch below). Diagnostics must use the same
		// effective localId so target-resolution checks match the apply
		// path's actual behaviour — otherwise we get false positives
		// (command.localId missing but insertAfterQueueLocalId valid) and
		// false negatives (the inverse).
		const effectiveTargetLocalId = normalizedTelemetryEnabled
			? insertAfterQueue && this.insertAfterQueueLocalId
				? this.insertAfterQueueLocalId
				: getTargetLocalId(command)
			: undefined;
		const preApplyLocalIds = normalizedTelemetryEnabled
			? this.runPreApplyDiagnostics(command, effectiveTargetLocalId)
			: undefined;

		// If we have subsequent "insertAfter" commands with the same localId, instead point to the end of the
		// previous insertion. This is because the AI will "insertAfter" in a logical order, but that puts the content
		// into the document backwards.
		if (insertAfterQueue && this.insertAfterQueueLocalId) {
			// If for some reason the localId doesn't exist in the document fallback to the original stream command
			try {
				isSuccessful = this.streamingCommands[command.type]({
					...command,
					localId: this.insertAfterQueueLocalId,
				});
			} catch {
				isSuccessful = this.streamingCommands[command.type](command);
			}
		} else {
			isSuccessful = this.streamingCommands[command.type](command);
		}

		if (normalizedTelemetryEnabled && preApplyLocalIds !== undefined) {
			this.runPostApplyDiagnostics(command, preApplyLocalIds, effectiveTargetLocalId);
		} else {
			// Gate off (or pre-apply snapshot was unavailable) — drop any
			// cached snapshot so it can't leak into the next gate-on call.
			this.lastPostApplyLocalIds = undefined;
		}

		return isSuccessful;
	}

	/**
	 * Apply-anomaly diagnostics: pre-apply phase. Snapshots top-level
	 * localIds and fires `local_id_not_found` if the command targets
	 * a localId that doesn't exist. Returns the snapshot for reuse by the
	 * post-apply phase.
	 *
	 * Reuses the previous post-apply snapshot when available — see the
	 * `lastPostApplyLocalIds` field for the cache invariants.
	 *
	 * Caller is responsible for the experiment gate check, and for
	 * computing `effectiveTargetLocalId` so it reflects what the apply
	 * path will actually act on (e.g. the substituted
	 * `insertAfterQueueLocalId` for chained `insertNodeAfter` calls).
	 */
	private runPreApplyDiagnostics(
		command: StreamingCommand,
		effectiveTargetLocalId: string | undefined,
	): Set<string> {
		// Recursive snapshot — every localId at every depth in the doc.
		// Reuses the previous post-apply snapshot when available; cached
		// snapshots are also full-depth (see `runPostApplyDiagnostics`).
		const preApplyLocalIds =
			this.lastPostApplyLocalIds ??
			collectAllLocalIds(this.docBeforeStreaming ?? undefined);

		// `local_id_not_found` detection uses `findNodeByLocalIdAndApi`
		// rather than checking the snapshot. The snapshot is `Set`-based
		// for fast post-apply diff comparisons, but for the pre-apply
		// "does this localId exist?" question we use the same recursive
		// walk every targeted command uses — that walk IS the apply
		// layer's lookup, so detection is ground-truth equivalent and
		// there are no false positives for nested targets.
		if (
			effectiveTargetLocalId !== undefined &&
			findNodeByLocalIdAndApi(this.api, effectiveTargetLocalId) === undefined
		) {
			fireToolCallAnomaly({
				api: this.api,
				traceId: this.traceId,
				commandType: command.type,
				symptom: 'local_id_not_found',
				targetLocalId: effectiveTargetLocalId,
			});
		}
		return preApplyLocalIds;
	}

	/**
	 * Apply-anomaly diagnostics: post-apply phase. Diffs the post-apply
	 * full-depth localId snapshot against the pre-apply snapshot (both
	 * built via `collectAllLocalIds`, so every depth is included) and
	 * fires:
	 * - `unexpected_node_removal` when nodes disappeared that the command
	 *   wasn't supposed to remove/relocate. Reports
	 *   `effectiveTargetLocalId` so the dashboard reader sees the localId
	 *   the apply path actually acted on.
	 * - `no_document_change` when a mutating command produced no document
	 *   change at all.
	 * Caches the post-apply snapshot for reuse by the next applyCommand.
	 *
	 * Caller is responsible for the experiment gate check, and for
	 * computing `effectiveTargetLocalId` so it reflects what the apply
	 * path actually acted on (see `runPreApplyDiagnostics`).
	 */
	private runPostApplyDiagnostics(
		command: StreamingCommand,
		preApplyLocalIds: Set<string>,
		effectiveTargetLocalId: string | undefined,
	): void {
		const postApplyDoc = getPmDoc(this.api);
		if (postApplyDoc === undefined) {
			// The editor view was unavailable post-apply (e.g. teardown
			// race). Skip both diff-based diagnostics — we cannot
			// distinguish "everything was removed" from "we couldn't
			// observe the post-apply state". Drop the cached snapshot too
			// so it can't poison the next command's pre-apply diagnostics
			// with a stale or empty set.
			this.lastPostApplyLocalIds = undefined;
			return;
		}
		// Recursive snapshot — captures every localId at every depth so
		// the diff catches collateral removals nested inside layouts,
		// expand blocks, panels, table cells, etc. The previous
		// top-level-only snapshot produced false positives for wrap /
		// moveNode (a previously-top-level node nested into a new parent
		// looked "removed") and false negatives for any genuinely
		// collateral removal of a deep node.
		const postApplyLocalIds = collectAllLocalIds(postApplyDoc);
		this.lastPostApplyLocalIds = postApplyLocalIds;

		// Pass `docBeforeStreaming` so the helper can expand each direct
		// target to include its descendant `localId`s. Required since
		// the diff above (`collectAllLocalIds`) is full-depth — removing
		// a container would otherwise spuriously fire
		// `unexpected_node_removal` for every nested descendant.
		const explicitTargets = extractExplicitRemovalTargets(
			command,
			this.docBeforeStreaming ?? undefined,
		);
		const removedLocalIds: string[] = [];
		preApplyLocalIds.forEach((id) => {
			if (!postApplyLocalIds.has(id) && !explicitTargets.has(id)) {
				removedLocalIds.push(id);
			}
		});
		if (removedLocalIds.length > 0) {
			fireToolCallAnomaly({
				api: this.api,
				traceId: this.traceId,
				commandType: command.type,
				symptom: 'unexpected_node_removal',
				targetLocalId: effectiveTargetLocalId,
				removedLocalIds,
			});
		}

		// Streamable commands (`replaceNode`, `replaceDoc`,
		// `insertNodeBefore`, `insertNodeAfter`, `appendNode`,
		// `appendNodeToEnd`) are two-phase: the apply call only sets a
		// selection (and sometimes inserts a placeholder paragraph), and
		// the actual content change arrives later via streaming chunks.
		// At apply time the document has legitimately not changed yet, so
		// firing `no_document_change` here would produce false positives
		// for every streamable command. The deferred check fires from
		// `checkAndFireNoDocumentChangeAnalytics` in `endExperiencePromise`
		// once all stream chunks have been applied.
		if (
			!isAStreamableCommand(command) &&
			this.docBeforeStreaming &&
			postApplyDoc.eq(this.docBeforeStreaming)
		) {
			fireToolCallAnomaly({
				api: this.api,
				traceId: this.traceId,
				commandType: command.type,
				symptom: 'no_document_change',
			});
		}
	}

	private initStream(command: StreamingCommand, options: InitOptions) {
		const applied = this.applyCommand(command);
		// If we couldn't apply the command (ie. broken localId) add it to the abort cache so we don't apply anymore commands
		if (!applied) {
			// Never start the stream and resolve immediately
			if (this.currentAction?.id) {
				this.updateAbortedCache([this.currentAction?.id]);
			}
			return true;
		}

		this.streamFlowPacer?.reset();
		this.adfStreamer?.reset();
		// We have to set an empty callback before starting otherwise the flow pacer wont start. We need to cleanup the flow pacer
		// and make it more generic.
		this.streamFlowPacer?.setEmit(() => {});
		this.streamFlowPacer?.start();
		const isCreate =
			(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
			command.streamingType === 'create';
		this.logger.logQueueState('Open AI Modal', { command });
		this.api.aiExperience.actions.openAIModal({
			invokedFrom: 'streamingApi',
			configItem: UNSTABLE_configItems.freeGenerateAifcConfigItem,
			withStreamingHighlight: !isCreate,
			// don't show accept reject screen for staging first-time creation streaming
			withAcceptReject: !isCreate,
			hideLoadingScreen:
				(fg('platform_editor_tmp_hide_loader') && options?.hideLoadingScreen) ?? false,
			traceId: this.traceId,
			singleInstrumentationId: this.singleInstrumentationId,
			isCreate,
			promptVersion: this.promptVersion,
		});
		if (!isAStreamableCommand(command)) {
			checkAndFireNoDocumentChangeAnalytics({
				api: this.api,
				command: this.currentCommand,
				docBeforeStreaming: this.docBeforeStreaming,
				traceId: this.traceId,
			});
		}
		return false;
	}

	private async initStreamPromise(command: StreamingCommand, options: InitOptions): Promise<void> {
		await new Promise<void>((resolve) => {
			this.logger.logQueueState(`Init Command ${command.type}`, { command });
			if (this.initStream(command, options)) {
				resolve();
				this.logger.logQueueState(`Init Command Resolved ${command.type}`, { command });
				return;
			}
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
				if (nextSharedState.aiDecorationExperiencePluginState.isRunning) {
					unsubscribe?.();
					resolve();
					this.logger.logQueueState(`Init Command Resolved ${command.type}`, { command });
				}
			});
		});
	}

	public startSession(
		sessionId: string,
		traceId?: string,
		singleInstrumentationId?: string,
		promptVersion?: string,
	): void {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = traceId;
			fireSessionStartedAnalytics({ api: this.api, sessionId, traceId, promptVersion });
		}
		this.singleInstrumentationId = singleInstrumentationId;
		this.promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
			? promptVersion
			: undefined;
		if (expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
			this.resetSessionOutcomeState();
			// Capture the doc as it was before any streaming command runs.
			// `analyzeOutcome` will diff this against the post-session doc
			// at end-of-session to compute structural-delta fields.
			this.preSessionDoc = getPmDoc(this.api);
		}
		this.logger.logPublic('Start Session', { sessionId, singleInstrumentationId });
	}

	public queueEndSession(sessionId: string): void {
		this.logger.logPublic('Queue End Session', { sessionId });
	}

	public endSession(sessionId: string): void {
		if (expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
			this.runPostApplyOutcomeAnalysis();
			this.fireSessionOutcomeOnce();
		}
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
		this.currentCommand = undefined;
		this.logger.logPublic('End Session', { sessionId });
	}

	/**
	 * Fire the authoritative `sessionOutcome` analytics event for the current
	 * session. Idempotent — calling this multiple times within the same session
	 * results in at most a single event being fired.
	 *
	 * If `lifecycleSymptom` is supplied, it overrides any repair-derived
	 * symptom and forces `outcome_class: 'failure'` — a session that never
	 * reached end-of-stream cannot be a success.
	 */
	private fireSessionOutcomeOnce(
		options: { lifecycleSymptom?: 'session_aborted' | 'session_timeout' } = {},
	): void {
		if (this.sessionOutcomeFired) {
			return;
		}
		this.sessionOutcomeFired = true;
		const { lifecycleSymptom } = options;

		const { anyJsonInvalid, anyAdfInvalid, unrepairedJsonCount, unrepairedAdfCount } =
			this.sessionRepairSignals;

		// Derive the session-level repair-signal contract reported on the
		// outcome event:
		// - `finalJsonInvalid` / `finalAdfInvalid`: did any action see that
		//   layer become invalid at all?
		// - `finalJsonRepaired` / `finalAdfRepaired`: was every invalid
		//   occurrence in the session recovered? (i.e. AND over the per-action
		//   `repaired` flags for actions where invalid=true). False when the
		//   layer was never invalid, matching the per-action semantic.
		const finalJsonInvalid = anyJsonInvalid;
		const finalAdfInvalid = anyAdfInvalid;
		const finalJsonRepaired = anyJsonInvalid && unrepairedJsonCount === 0;
		const finalAdfRepaired = anyAdfInvalid && unrepairedAdfCount === 0;

		// Post-apply analysis flags. Read from the analyzer result computed
		// in `runPostApplyOutcomeAnalysis`. Each flag drives one suspicious-
		// signal event AND (if the session would otherwise have succeeded)
		// upgrades the outcome class to `success_but_suspicious`.
		//
		// `noMeaningfulEffect` only counts when the session actually ran a
		// mutating tool call — a read-only session that happens to leave
		// the doc unchanged is not suspicious, it's expected.
		const analysis = this.lastOutcomeAnalysis;
		const hadMutatingToolCall = this.sessionToolCalls.some((tc) =>
			MUTATING_COMMAND_TYPES.has(tc.commandType),
		);
		const unintendedRegionRemoved = analysis !== undefined && analysis.unintendedRegionRemoved;
		const unintendedRegionModified = analysis !== undefined && analysis.unintendedRegionModified;
		// `textCharacterDelta` tolerance: we treat ±1 character as
		// negligible because the apply layer can legitimately inject or
		// strip a single trailing whitespace / newline / punctuation
		// character during ProseMirror normalisation (e.g. closing a block
		// node, collapsing adjacent whitespace, trimming a trailing space
		// the model emitted). Strict equality to 0 would generate
		// false-positive `noMeaningfulEffect` events for sessions whose
		// only effect was a one-character normalisation artefact. A wider
		// tolerance would risk hiding real (but tiny) edits.
		const noMeaningfulEffect =
			analysis !== undefined &&
			hadMutatingToolCall &&
			analysis.totalNodeDelta === 0 &&
			Math.abs(analysis.textCharacterDelta) <= 1;
		const intentEffectMismatch = analysis !== undefined && analysis.intentEffectMismatch;

		// Outcome class derivation per the design doc (Phase 1 + 3):
		// - failure                : any layer (ADF or JSON) was invalid and
		//                            could not be repaired, OR the session
		//                            ended via a lifecycle event (abort /
		//                            timeout)
		// - success_with_repairs   : a layer was invalid but every invalid
		//                            occurrence was recovered
		// - clean_success          : no repair signals fired at all
		// - success_but_suspicious : the session would otherwise have been
		//                            clean_success or success_with_repairs
		//                            but the post-apply analyzer flagged
		//                            one or more suspicious signals
		// `failure` is never overridden by a suspicious signal — an
		// unrecovered apply error is more important than "looks fishy".
		const adfFailed = finalAdfInvalid && !finalAdfRepaired;
		const jsonFailed = finalJsonInvalid && !finalJsonRepaired;
		const anySuspiciousSignal =
			unintendedRegionRemoved ||
			unintendedRegionModified ||
			noMeaningfulEffect ||
			intentEffectMismatch;
		let outcomeClass: OutcomeClass;
		if (lifecycleSymptom !== undefined || adfFailed || jsonFailed) {
			outcomeClass = 'failure';
		} else if (anySuspiciousSignal) {
			outcomeClass = 'success_but_suspicious';
		} else if (finalAdfRepaired || finalJsonRepaired) {
			outcomeClass = 'success_with_repairs';
		} else {
			outcomeClass = 'clean_success';
		}

		// Compute a small histogram of command types to help debug
		// no-effect sessions ("what was being attempted?"). Keys are
		// orchestrator command-type constants (not user content). Scoped
		// deliberately to `noMeaningfulEffect` sessions — avoids inflating
		// every sessionOutcome payload with histogram data that is only
		// useful when the session produced no meaningful change.
		let commandTypeCounts: Record<string, number> | undefined;
		if (noMeaningfulEffect) {
			commandTypeCounts = {};
			for (const tc of this.sessionToolCalls) {
				commandTypeCounts[tc.commandType] = (commandTypeCounts[tc.commandType] ?? 0) + 1;
			}
		}

		// True when any of the suspicious signals that fired came from a
		// heuristic rather than a backend-supplied label. Currently always
		// true for `unintendedRegionModified` (text-length proxy) and
		// `intentEffectMismatch` (intent inferred from tool-call mix).
		// Lets dashboards filter the noisier inferred signals out of
		// headline KPIs.
		const heuristicInferred = unintendedRegionModified || intentEffectMismatch ? true : undefined;

		// Pick the worst (symptom, detectionLayer) we observed: lifecycle >
		// unrecovered failure > successful repair > clean. `detectionLayer`
		// names where the symptom was *first surfaced* (parser for repair
		// signals, orchestrator for apply / lifecycle), not where this
		// event is fired. Unmapped pairs fall back to `unknown`/`low`.
		let detectionLayer: DetectionLayer = 'orchestrator';
		let symptom: string | undefined;
		if (lifecycleSymptom !== undefined) {
			// Lifecycle symptoms are orchestrator-owned (abort / timeout).
			detectionLayer = 'orchestrator';
			symptom = lifecycleSymptom;
		} else if (adfFailed) {
			// The orchestrator surfaced an unrecovered ADF schema failure
			// when it tried to apply the streamed ADF.
			detectionLayer = 'orchestrator';
			symptom = 'apply_failed_adf_schema_error';
		} else if (jsonFailed) {
			// Parser-layer detection. There is no dedicated routing-table
			// entry for an unrecovered JSON failure today; we still attach
			// the symptom so dashboards can see it, and it routes to
			// `unknown`/`low` until the routing table grows an entry
			// covering this case.
			detectionLayer = 'parser';
			symptom = 'final_json_invalid';
		} else if (finalAdfRepaired) {
			// ADF repair is the higher-severity success signal — checked
			// before JSON repair so a session that observed both still
			// surfaces the more impactful symptom (ADF/high) rather than
			// the weaker JSON/medium one. Preserves the "worst observed
			// signal wins" invariant.
			detectionLayer = 'parser';
			symptom = 'final_adf_invalid';
		} else if (finalJsonRepaired) {
			detectionLayer = 'parser';
			symptom = 'final_json_repaired';
		} else if (anySuspiciousSignal) {
			// Pick the highest-severity suspicious signal observed — worst
			// signal wins, matching the same invariant used for repair/failure
			// signals above. The four signals are checked in severity order:
			// unintendedRegionRemoved (content disappeared) > unintendedRegionModified
			// (content changed unexpectedly) > noMeaningfulEffect (no net change) >
			// intentEffectMismatch (structural delta contradicts inferred intent).
			detectionLayer = 'post_apply';
			if (unintendedRegionRemoved) {
				symptom = 'unexpected_content_removal';
			} else if (unintendedRegionModified) {
				symptom = 'unintended_text_modification';
			} else if (noMeaningfulEffect) {
				symptom = 'no_meaningful_effect';
			} else {
				// intentEffectMismatch
				symptom = 'semantic_mismatch';
			}
		}

		fireSessionOutcomeAnalytics({
			api: this.api,
			traceId: this.traceId,
			outcomeClass,
			detectionLayer,
			symptom,
			finalJsonInvalid,
			finalJsonRepaired,
			finalAdfInvalid,
			finalAdfRepaired,
			// Forward the post-apply analyzer flags so dashboards can see
			// "what happened" and "why it was suspicious" on a single
			// event. Each is `undefined` when the analyzer didn't run
			// (e.g. session aborted before any apply).
			...(analysis !== undefined
				? {
						unintendedRegionRemoved,
						unintendedRegionModified,
						noMeaningfulEffect,
						intentEffectMismatch,
						// Raw deltas — only attached when the corresponding
						// suspicious flag is true, to avoid forwarding
						// meaningless zeros on clean sessions.
						...(unintendedRegionRemoved
							? {
									removedNodeCount: analysis.removedNodeCount,
									totalNodeDelta: analysis.totalNodeDelta,
									// Indicates whether unintendedRegionRemoved used
									// backend-supplied selectionLocalIds (precise) or
									// the proxy fallback. Backend plumbing arrives with
									// CCI-15978; until then always false.
									selectionLocalIdsAvailable: false,
								}
							: {}),
						...(unintendedRegionModified
							? { textCharacterDelta: analysis.textCharacterDelta }
							: {}),
					}
				: {}),
			...(commandTypeCounts !== undefined ? { commandTypeCounts } : {}),
			...(heuristicInferred !== undefined ? { heuristicInferred } : {}),
		});
	}

	/**
	 * Reset session-outcome bookkeeping so the next session starts from a clean
	 * slate. Called from `startSession`, `destroy`, and `abort`.
	 */
	private resetSessionOutcomeState(): void {
		this.sessionRepairSignals = {
			anyJsonInvalid: false,
			anyAdfInvalid: false,
			unrepairedJsonCount: 0,
			unrepairedAdfCount: 0,
		};
		this.sessionOutcomeFired = false;
		// Apply-anomaly diagnostics carry their own per-apply state. Drop
		// the cached snapshot at session boundaries so the next session's
		// first applyCommand can't compare against a stale doc reference.
		this.lastPostApplyLocalIds = undefined;
		// Post-apply outcome analyzer state. Cleared so the next session
		// starts with an empty tool-call record and no stale analysis.
		this.sessionToolCalls = [];
		this.lastOutcomeAnalysis = undefined;
		this.preSessionDoc = undefined;
	}

	/**
	 * Compute the structural-delta analysis for the just-completed session
	 * and stash it on `lastOutcomeAnalysis`. Called from `endSession`
	 * (gated). `fireSessionOutcomeOnce` reads `lastOutcomeAnalysis` to
	 * derive the `success_but_suspicious` outcome class and to fire the
	 * three suspicious-signal session-scope events.
	 *
	 * Skips silently if either the pre-session or post-session doc is
	 * unavailable (e.g. editor view torn down mid-stream) — partial-data
	 * analysis would produce misleading deltas.
	 */
	private runPostApplyOutcomeAnalysis(): void {
		const postDoc = getPmDoc(this.api);
		if (this.preSessionDoc === undefined || postDoc === undefined) {
			return;
		}
		this.lastOutcomeAnalysis = analyzeOutcome({
			preSessionAdf: this.preSessionDoc.toJSON() as DocLike,
			postSessionAdf: postDoc.toJSON() as DocLike,
			toolCalls: this.sessionToolCalls,
		});
	}

	public destroy(): void {
		this.queue = [];
		this.adfStreamer = undefined;
		this.currentCommand = undefined;
		this.docBeforeStreaming = undefined;
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
		this.promptVersion = undefined;
		if (expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
			this.resetSessionOutcomeState();
		}
	}

	private isAborted = false;
	/**
	 * Abort the current streaming and end the experience
	 * This is a hard abort and will clear the queue
	 * This will only restart the experience when initStreamCommand is called again
	 */
	public abort(): void {
		this.logger.logPublic('Abort');
		// Fire BEFORE any state teardown so `traceId` and
		// `singleInstrumentationId` are still set and the event can be
		// correlated in dashboards (matches the timeout / clean-drain paths).
		// We deliberately do NOT reset `sessionOutcomeFired` here — the
		// guard must persist through any follow-up `endSession` / timeout
		// for this same session; the next `startSession()` resets it.
		if (expValEquals('platform_editor_ai_normalized_telemetry', 'isEnabled', true)) {
			this.fireSessionOutcomeOnce({ lifecycleSymptom: 'session_aborted' });
		}
		const currentQueue = this.queue.map((cmd) => cmd.actionId);
		this.queue = [];

		// Add new aborted action IDs to end of array
		this.updateAbortedCache([...new Set(currentQueue), ...this.inProcessing]);
		this.isAborted = true;
		this.endExperience();
		this.isPendingResolveQueue = false;
		if (this.queueTimeout) {
			clearTimeout(this.queueTimeout);
		}
		this.currentAction = null;
		this.isFinalStream = false;
		this.currentCommand = undefined;
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			this.traceId = undefined;
		}
		this.singleInstrumentationId = undefined;
	}

	private updateAbortedCache(actionIds: string[]) {
		this.aborted = [...new Set([...this.aborted, ...actionIds])];
		const maxAbortedCacheLength = 5;
		if (this.aborted.length > maxAbortedCacheLength) {
			this.aborted = this.aborted.slice(-maxAbortedCacheLength);
		}
	}
}
